<template>
  <div class="flex h-screen bg-gray-900 text-white">
    <!-- Sidebar -->
    <aside class="w-64 bg-gray-800 text-white p-6 flex flex-col space-y-6 shadow-lg border-r border-gray-700">
      <div class="flex items-center justify-center">
        <img src="@/assets/logo.svg" alt="Vultric Logo" class="h-20" />
      </div>
      <Navbar />
    </aside>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col">
      <!-- Header -->
      <header class="h-16 bg-gray-800 flex items-center justify-between px-6 shadow-md border-b border-gray-700">
        <h2 class="text-xl font-semibold">View Ticket</h2>
      </header>

      <main class="flex-1 overflow-y-auto p-6 space-y-6">
        <!-- Ticket Overview -->
        <div class="bg-gray-800 rounded-lg p-6 shadow border border-gray-700 flex justify-between items-start">
          <div>
            <h3 class="text-lg font-bold mb-2">#{{ ticket.id }} - {{ ticket.subject }}</h3>
            <p class="mb-1"><strong>Priority:</strong> {{ ticket.priority }}</p>
            <p class="mb-1"><strong>Last Updated:</strong> {{ ticket.updated_at }}</p>
          </div>

          <div class="flex flex-col items-end space-y-2">
            <span
              class="px-3 py-1 text-sm font-semibold rounded-full"
              :class="ticket.status === 'Open' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'"
            >
              {{ ticket.status }}
            </span>
            <button
              @click="toggleTicketStatus"
              class="px-3 py-1 text-sm font-semibold rounded-lg hover:bg-gray-700 bg-gray-700"
            >
              {{ ticket.status === 'Open' ? 'Close Ticket' : 'Reopen Ticket' }}
            </button>
          </div>
        </div>

        <!-- Messages Section -->
        <div class="bg-gray-800 rounded-lg p-6 shadow border border-gray-700">
          <h4 class="text-md font-semibold mb-4">Ticket History</h4>

          <div v-if="messages.length === 0" class="text-gray-400 text-center py-10">
            No messages yet.
          </div>

          <div
            v-for="message in messages"
            :key="message.id"
            class="mb-6 border border-gray-700 rounded-lg overflow-hidden"
          >
            <!-- Message Header -->
            <div
              class="px-4 py-2 flex justify-between items-center text-sm font-medium"
              :class="message.sender === 'support' ? 'bg-blue-800 text-blue-200' : 'bg-gray-700 text-gray-200'"
            >
              <span>{{ message.sender === 'support' ? 'Support Team' : 'You' }}</span>
              <span class="text-xs text-gray-400">{{ formatTimestamp(message.timestamp) }}</span>
            </div>

            <!-- Message Body -->
            <div class="p-4 bg-gray-800">
              <p class="whitespace-pre-line">{{ message.text }}</p>
            </div>
          </div>
        </div>

        <!-- Reply Box -->
        <div class="bg-gray-800 rounded-lg p-6 shadow border border-gray-700">
          <h4 class="text-md font-semibold mb-3">Post a Reply</h4>
          <textarea
            v-model="newMessage"
            rows="5"
            class="w-full resize-none p-3 rounded-lg bg-gray-900 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder="Type your reply here..."
            :disabled="ticket.status === 'Closed'"
          ></textarea>
          <button
            @click="sendMessage"
            class="mt-3 px-5 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="ticket.status === 'Closed'"
          >
            Send Reply
          </button>
          <p v-if="ticket.status === 'Closed'" class="text-red-500 text-sm mt-2">
            This ticket has been closed. You cannot reply.
          </p>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/nav.vue';
import axios from 'axios';

export default {
  name: "CloudSupportTicket",
  components: { Navbar },
  data() {
    return {
      ticket: {},
      messages: [],
      newMessage: ""
    };
  },
  async created() {
    await this.fetchTicket();
    await this.fetchMessages();
  },
  methods: {
    async fetchTicket() {
      try {
        const response = await axios.get(
          `https://api.syniac.com:3000/api/ticket/${this.$route.params.id}`,
          { withCredentials: true }
        );
        this.ticket = response.data;
      } catch (error) {
        console.error("Error fetching ticket:", error);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.$router.push('/support');
        }
      }
    },
    async fetchMessages() {
      try {
        const response = await axios.get(
          `https://api.syniac.com:3000/api/ticket/${this.$route.params.id}/messages`,
          { withCredentials: true }
        );
        this.messages = response.data;
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return;
      try {
        await axios.post(
          `https://api.syniac.com:3000/api/ticket/${this.ticket.id}/messages`,
          { text: this.newMessage, sender: "user" },
          { withCredentials: true }
        );
        this.messages.push({
          id: this.messages.length + 1,
          sender: 'user',
          text: this.newMessage,
          timestamp: new Date().toISOString()
        });
        this.newMessage = "";
      } catch (error) {
        console.error("Error sending message:", error);
      }
    },
    async toggleTicketStatus() {
      try {
        const newStatus = this.ticket.status === 'Open' ? 'Closed' : 'Open';
        await axios.put(
          `https://api.syniac.com:3000/api/ticket/${this.ticket.id}/status`,
          { status: newStatus },
          { withCredentials: true }
        );
        this.ticket.status = newStatus;
      } catch (error) {
        console.error("Error updating ticket status:", error);
      }
    },
    formatTimestamp(timestamp) {
      return new Date(timestamp).toLocaleString();
    }
  }
};
</script>
